import {
    faBoxCircleCheck,
    faBoxOpen,
    faCirclePause,
    faPlay,
    faPlus,
    faTrash,
    IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import Flex from 'components/ui/Flex';
import useAppSelector from 'hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { ReturnStatusType } from 'returnCenter-api';
import { returnSelectors } from 'store/reducers/returnSlice';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ActionButton from 'components/ui/ActionButton';
import SaveChangesButton from './SaveAction';
import CompleteReturn from './CompleteReturn';
import CompleteBatchReturn from './CompleteBatchReturn';
import Button from 'components/ui/Button';
import NewReturnActions from './NewReturnActions';
import CollapsibleSection from 'components/ui/CollapsibleSection';

type ActionConfig = {
    icon: IconDefinition;
    labelKey: string;
    statusNameKey: string; // short status name used in "Markera som [status]"
    status: ReturnStatusType;
};

// Primary action per status — the most logical next step
const PRIMARY_ACTION: Partial<Record<ReturnStatusType, ActionConfig>> = {
    [ReturnStatusType.Created]: {
        icon: faBoxOpen,
        labelKey: 'incomingPackage',
        statusNameKey: 'statusIncomingPackage',
        status: ReturnStatusType.IncomingPackage,
    },
    [ReturnStatusType.IncomingPackage]: {
        icon: faBoxCircleCheck,
        labelKey: 'received',
        statusNameKey: 'statusReceived',
        status: ReturnStatusType.Received,
    },
    [ReturnStatusType.Received]: {
        icon: faPlay,
        labelKey: 'statusInProgress',
        statusNameKey: 'statusInProgress',
        status: ReturnStatusType.InProgress,
    },
    [ReturnStatusType.OnHold]: {
        icon: faPlay,
        labelKey: 'statusInProgress',
        statusNameKey: 'statusInProgress',
        status: ReturnStatusType.InProgress,
    },
};

// Actions that are always in "more" — previous/adjacent steps + utilities
const MORE_ACTIONS: ActionConfig[] = [
    {
        icon: faPlus,
        labelKey: 'createReturn',
        statusNameKey: 'statusCreated',
        status: ReturnStatusType.Created,
    },
    {
        icon: faCirclePause,
        labelKey: 'pauseReturn',
        statusNameKey: 'statusOnHold',
        status: ReturnStatusType.OnHold,
    },
    {
        icon: faBoxOpen,
        labelKey: 'incomingPackage',
        statusNameKey: 'statusIncomingPackage',
        status: ReturnStatusType.IncomingPackage,
    },
    {
        icon: faBoxCircleCheck,
        labelKey: 'goodsReceived',
        statusNameKey: 'statusReceived',
        status: ReturnStatusType.Received,
    },
    {
        icon: faPlay,
        labelKey: 'setToInProgress',
        statusNameKey: 'statusInProgress',
        status: ReturnStatusType.InProgress,
    },
];

type ReturnActionPanelProps = {
    isNewReturn?: boolean;
    onSaveWithStatus: (status: ReturnStatusType) => void;
    onDelete: () => void;
    onComplete: () => void;
    onCompleteBatch?: () => void;
};

const ReturnActionPanel = ({ isNewReturn, onSaveWithStatus, onDelete, onComplete, onCompleteBatch }: ReturnActionPanelProps) => {
    const { t } = useTranslation();

    const buttonStates = useAppSelector(returnSelectors.selectReturnButtonStates);
    const currentStatus = useAppSelector(returnSelectors.selectReturnSlipStatus);

    const isCompleted = currentStatus === ReturnStatusType.Completed;
    const isCreditedBatchPending = currentStatus === ReturnStatusType.CreditedBatchPending;
    const isInProgress = currentStatus === ReturnStatusType.InProgress;
    const isProcessing = currentStatus === ReturnStatusType.Processing;

    const primaryAction = currentStatus != null ? PRIMARY_ACTION[currentStatus] : undefined;
    const showPrimaryAction = primaryAction && !isCompleted && !isInProgress && !isCreditedBatchPending;

    const visibleMore = MORE_ACTIONS.filter(
        (a) => a.status !== currentStatus && (!showPrimaryAction || a.status !== primaryAction?.status),
    );

    if (isNewReturn) {
        return (
            <NewReturnActions
                isCompleted={isCompleted}
                onSaveWithStatus={onSaveWithStatus}
                onComplete={onComplete}
                isLoading={buttonStates.isLoading}
            />
        );
    }

    if (isCreditedBatchPending) {
        return (
            <Flex column gap="1rem">
                <TransparentPanel gap="0.5rem" column>
                    <Panel className="p-3" column gap="0.5rem">
                        <SaveChangesButton
                            onClick={() => onSaveWithStatus(ReturnStatusType.CreditedBatchPending)}
                            disabled={!buttonStates.canSave}
                            isLoading={buttonStates.isLoading}
                            title={t('saveChangesTooltip')}
                        />

                        <CompleteBatchReturn
                            onComplete={onCompleteBatch ?? (() => {})}
                            disabled={!buttonStates.canCompleteBatch}
                            isLoading={buttonStates.isLoading}
                        />
                    </Panel>
                </TransparentPanel>
            </Flex>
        );
    }

    return (
        <Flex column gap="1rem">
            {/* Primary action if applicable */}
            {showPrimaryAction && (
                <Panel className="p-3" column gap="0.5rem">
                    <NextStepLabel>{t('nextStepInFlow')}</NextStepLabel>
                    <ActionButton
                        label={`${t('markAs')} ${t(primaryAction.statusNameKey, t(primaryAction.labelKey))}`}
                        onClick={() => onSaveWithStatus(primaryAction.status)}
                        disabled={isCompleted || !buttonStates.canModify}
                        isLoading={buttonStates.isLoading}
                        main
                        fullWidth
                    />
                </Panel>
            )}
            {/* Save & Complete */}
            <TransparentPanel gap="0.5rem" column>
                <Panel className="p-3" column gap="0.5rem">
                    <SaveChangesButton
                        onClick={() => onSaveWithStatus(currentStatus ?? ReturnStatusType.Created)}
                        disabled={isCompleted || !buttonStates.canSave}
                        isLoading={buttonStates.isLoading}
                        title={t('saveChangesTooltip')}
                    />

                    <CompleteReturn
                        onComplete={onComplete}
                        disabled={isCompleted || isProcessing || !buttonStates.canComplete}
                        isLoading={buttonStates.isLoading}
                    />
                </Panel>

                {/* More actions */}
                {visibleMore.length > 0 && (
                    <Panel column gap="0">
                        <CollapsibleSection
                            openLabel={t('moreActions')}
                            closeLabel={t('lessActions')}
                            justify="between"
                        >
                            <MoreList column>
                                {visibleMore.map((action) => (
                                    <MoreItem
                                        key={action.status}
                                        $open={true}
                                        onClick={() => onSaveWithStatus(action.status)}
                                        disabled={isCompleted || !buttonStates.canModify}
                                    >
                                        <StyledIcon align="center">
                                            <FontAwesomeIcon icon={action.icon} />
                                        </StyledIcon>
                                        <span>{t(action.labelKey)}</span>
                                    </MoreItem>
                                ))}
                                <DeleteButton onClick={onDelete} disabled={!buttonStates.canDelete}>
                                    <StyledIcon align="center">
                                        <FontAwesomeIcon icon={faTrash} />
                                    </StyledIcon>
                                    <span>{t('cancelReturn')}</span>
                                </DeleteButton>
                            </MoreList>
                        </CollapsibleSection>
                    </Panel>
                )}
            </TransparentPanel>
        </Flex>
    );
};

export default ReturnActionPanel;

const TransparentPanel = styled(Flex)`
    background: transparent;
    border: none;
`;

const Panel = styled(Flex)`
    background: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
`;

const MoreList = styled(Flex)`
    padding: 0 1rem 1rem 1rem;
`;

const MoreItem = styled.button<{ $open: boolean }>`
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 9px 14px;
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: background 0.12s;

    &:last-child {
        border-bottom: none;
    }
    &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.fillLight};
    }
    &:disabled {
        opacity: 0.4;
        cursor: text;
    }
`;

const StyledIcon = styled(Flex)`
    width: 20px;
    height: 20px;
`;

const DeleteButton = styled(Button)`
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    background: ${({ theme }) => theme.colors.error};
    border-radius: var(--radius);
    color: ${({ theme }) => theme.colors.fillWhite} !important;
    padding: 9px 14px;
    margin-top: 8px;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.rejected};
    }
    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const NextStepLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textFaded};
`;
